// TODO: Check how this can get improved after the migration.
// We usually do not want to include the complete client SDK in our code by default.
// Note: We cannot import individual functions as the teams SDK assumes microsoftTeams to be set
// with all available functions
import * as microsoftTeams from "@microsoft/teams-js";

import api from "@/api";
import deepClone from "@/utils/deep-clone";
import router from "@/router";

/**
 * Constants
 */
const ACCESS_TOKEN_NAME = "leapsomeToken";
const ACCESS_TOKEN_NAME_IMPERSONATED = "leapsomeImpersonationToken";
const DEFAULT_AUTH_STRATEGY = {
    // saml, google, local, notFound
    strategy: undefined,
    team: undefined,
    teamName: undefined,
};

const state = {
    initialized: false,
    authStrategy: undefined,
    invite: undefined,
    token: undefined,
    tokenPayload: undefined,
    sessionRefreshTimeout: undefined,
};

/**
 * Getters
 */
const getters = {
    initialized(state) {
        return state.initialized;
    },
    isLoggedIn(state) {
        return !!state.token;
    },
    authStrategy(state) {
        return deepClone(state.authStrategy || DEFAULT_AUTH_STRATEGY);
    },
    invite(state) {
        return deepClone(state.invite) || null;
    },
};

/**
 * Actions
 */
const actions = {
    async init({ dispatch, commit, state }) {
        if (state.initialized) return;

        const token =
            window.localStorage.getItem(ACCESS_TOKEN_NAME_IMPERSONATED) ||
            window.localStorage.getItem(ACCESS_TOKEN_NAME);

        if (!token) {
            // guest user
            // NOTE: Do not trigger a logout as Google sign-in will fail because we are
            // resetting the user data
            commit("setInitializedStatus", true);
            return;
        }

        if (!_isValidTokenFormat(token)) {
            // guest user
            await dispatch("logout");
            return;
        }

        const tokenPayload = _parseToken(token);

        // check expiry
        if (!tokenPayload.exp || tokenPayload.exp <= Date.now() / 1000) {
            await dispatch("logout");
            return;
        }

        await dispatch("updateSession", { payload: { token: token } });

        // we always want to update the token after initial load to
        // have proper stats and up2date user timezone data
        await dispatch("performSessionRefresh");
    },
    checkMsTeams({ state }, { payload: { token = "" } } = { payload: {} }) {
        _microsoftTeamsInterceptor(token || state.token);
    },
    getAuthStrategy({ commit }, { payload: { username = "" } } = { payload: {} }) {
        return api.post("/api/users/auth/identify", { username }).then(({ data }) => {
            commit("setAuthStrategy", data);
        });
    },
    resetAuthStrategy({ commit }) {
        commit("setAuthStrategy", null);
    },
    getInvite({ commit }, { payload: { teamId = "", inviteToken = "" } } = { payload: {} }) {
        return api
            .get(`/api/users/get/invite/${teamId}/${inviteToken}`)
            .then(({ data }) => {
                commit("setInvite", data);
            })
            .catch(() => {
                router.push("/auth/login");
            });
    },
    createUser(
        { dispatch, rootState, state },
        {
            payload: {
                firstname = "",
                lang = "en",
                lastname = "",
                password = "",
                privacy = false,
                username = "",
                invite = { token: "" },
                samlLink = "",
            },
        } = { payload: {} },
    ) {
        return api
            .post("/api/users/create/user", {
                firstname,
                lang,
                lastname,
                password,
                privacy,
                username,
                invite,
            })
            .then(async ({ data }) => {
                // this link is provided only when SSO is enforced
                if (samlLink) {
                    window.location = samlLink;
                } else {
                    await dispatch("updateSession", { payload: { token: data.token } });
                    _handleRedirect(rootState.users?.current?.info, state.token);
                }
            });
    },
    passwordResetRequest(_, { payload: { username = "" } } = { payload: {} }) {
        return api.post("/api/users/update/password/request", { username });
    },
    passwordReset(
        { dispatch, rootState, state },
        { payload: { password = "", passwordToken = "", user = "" } } = { payload: {} },
    ) {
        return api
            .post("/api/users/update/password/reset", { password, passwordToken, user })
            .then(async ({ data }) => {
                await dispatch("updateSession", { payload: { token: data.token } });
                _handleRedirect(rootState.users?.current?.info, state.token);
            });
    },
    performLocalAuth(
        { dispatch, rootState, state },
        { payload: { username = "", password = "" } } = { payload: {} },
    ) {
        return api.post("/api/users/auth/local", { username, password }).then(async ({ data }) => {
            await dispatch("updateSession", { payload: { token: data.token } });
            _handleRedirect(rootState.users?.current?.info, state.token);
        });
    },
    performTokenAuth(
        { dispatch, rootState, state },
        { payload: { userId = "", token = "" } } = { payload: {} },
    ) {
        return api.post("/api/users/auth/token", { user: userId, token }).then(async ({ data }) => {
            await dispatch("updateSession", { payload: { token: data.token } });
            _handleRedirect(rootState.users?.current?.info, state.token);
        });
    },
    performSessionRefresh({ state, dispatch }) {
        let timeZone = Intl?.DateTimeFormat?.().resolvedOptions().timeZone ?? null;

        return api
            .post("/api/users/update/token", { _id: state.tokenPayload._id, tz: timeZone })
            .then(({ data }) => {
                dispatch("updateSession", { payload: { token: data.token } });
            })
            .catch(() => {
                dispatch("logout");
            });
    },
    async updateSession(
        { commit, dispatch, state },
        { payload: { token = "" } } = { payload: {} },
    ) {
        if (!token) {
            dispatch("logout");
            return;
        }

        commit("setToken", {
            token,
            callback: () => {
                dispatch("performSessionRefresh");
            },
        });

        // get user details
        await dispatch(
            "users/current/getInfoFromToken",
            { payload: { tokenPayload: state.tokenPayload } },
            { root: true },
        );
    },
    async logout({ commit, dispatch }) {
        const isImpersonatedSession = !!state.tokenPayload?.impersonated;

        // reset current user token
        commit("setToken");

        if (isImpersonatedSession) {
            // navigates to different app such that an additional state reset
            // is not required
            window.location.replace("/admin/#/teams");
        } else {
            // user details are reset in here only as there are many watchers/getters
            // that react to changes before a redirect would happen
            await dispatch(
                "users/current/getInfoFromToken",
                { payload: { tokenPayload: state.tokenPayload } },
                { root: true },
            );

            router.push("/auth/login", () => {
                // TODO: Introduce a generic mechanism to reset state values to a default
                //
                // We use some state caching and don't have a central place to reset the
                // state values when changing your account. This is a quick (& ugly :/) fix to
                // make sure we always start with a clean state if you sign out from one account.
                window.location.reload();
            });
        }
    },
};

/**
 * Mutations
 */

const mutations = {
    setAuthStrategy(state, authStrategy = undefined) {
        state.authStrategy = authStrategy;
    },
    setInvite(state, invite = undefined) {
        state.invite = invite;
    },
    setInitializedStatus(state, value = true) {
        state.initialized = value;
    },
    setToken(state, { token = "", callback = undefined } = {}) {
        if (!token) {
            // reset auth state, but keep original access token stored if impersonation is active
            if (!state.tokenPayload?.impersonated)
                window.localStorage.removeItem(ACCESS_TOKEN_NAME);
            window.localStorage.removeItem(ACCESS_TOKEN_NAME_IMPERSONATED);
            delete api.defaults.headers.common["Authorization"];
            state.token = undefined;
            state.tokenPayload = undefined;
            state.authStrategy = undefined;

            clearTimeout(state.sessionRefreshTimeout);
        } else {
            api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
            state.token = token;
            state.tokenPayload = _parseToken(token);

            if (state.tokenPayload?.impersonated) {
                window.localStorage.setItem(ACCESS_TOKEN_NAME_IMPERSONATED, token);
            } else {
                window.localStorage.setItem(ACCESS_TOKEN_NAME, token);
            }

            if (typeof callback === "function") {
                const remainingTimeInSeconds = state.tokenPayload?.exp - Date.now() / 1000 - 60 * 2;
                state.sessionRefreshTimeout = setTimeout(callback, 1000 * remainingTimeInSeconds);
            }
        }

        // at least triggered once
        state.initialized = true;
    },
};

/**
 * Helpers
 */
const _isValidTokenFormat = token => {
    return Boolean(token && token.includes("."));
};

const _parseToken = token => {
    try {
        return JSON.parse(
            window.atob(
                token
                    .split(".")[1]
                    .replace(/-/g, "+")
                    .replace(/_/g, "/"),
            ),
        );
    } catch {
        return {};
    }
};

const _handleRedirect = (currentUser = {}, token = "") => {
    _microsoftTeamsInterceptor(token);

    if (window.location.hash.includes("?redirect=")) {
        var redirectTo = decodeURIComponent(
            window.location.hash.slice(window.location.hash.indexOf("?")).replace("?redirect=", ""),
        );

        window.location.replace("/app/#" + redirectTo);
    } else if (currentUser.rights.AM_LIST) {
        window.location.replace("/admin/#/teams");
    } else {
        router.push("/profile");
    }
};

// triggered if in msTeams iframe
const _microsoftTeamsInterceptor = (token = "") => {
    microsoftTeams.initialize();
    microsoftTeams.getContext(ctx => {
        let authType = "install";
        if (ctx.entityId === "leapsome") authType = "personal";

        window.location.assign(
            `/api/msteams/auth/init/${authType}?jwt=${token}&entityId=${ctx.entityId}&tid=${ctx.tid}&groupId=${ctx.groupId}`,
        );
    });
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
